import * as React from 'react';
import classNames from 'classnames';
import Highlight from 'react-highlighter';

import './styles.scss';

import { findDOMNode } from 'react-dom';
import { Classes, MenuDivider, Popover, Position as BlueprintPosition, Position } from '@blueprintjs/core';
import { drop, findIndex, takeWhile } from 'lodash';

import Icon from '../Icon';
import Menu from '../Menu';
import MenuItem from '../MenuItem';
import ControlInput from '../ControlInput';
import { scrollTo } from '../../Utilities/ScrollTo';
import { getScrollParent, offsetTill } from '../../Utilities/Generic';

export interface IControlSelectProps {
    /** placeholder */
    placeholder?: string;
    /** Controlled value */
    value: string;
    /** Disabled status */
    disabled?: boolean;
    /** Focus handler */
    onFocus?: (event) => void;
    /** Blur handler */
    onBlur?: (event) => void;
    /** Change handler */
    onChange?: (event) => void;
    /** Click handler */
    onClick?: (event) => void;
    /** Focus field on mount */
    autoFocus?: boolean;
    /** Color higlight for errors */
    hasError?: boolean;
    /** Layout to use */
    layout?: 'default' | 'select' | 'pillbutton' | 'menu';
    /** HTML options list */
    children: any;
    /** className to append */
    className?: string;
    /** Disable search */
    disableSearch?: boolean;
    /** Data test id for automated tests */
    testId?: string;
    /** Selects all text in searchfield on focus */
    selectAllOnFocus?: boolean;
    /** Size limit */
    size?: 'default' | 'small';
    /** DOM target element name attribute */
    name?: string;
    /** DOM target element id attribute */
    id?: string;
    /** Disable clearing */
    disableClear?: boolean;
    /** Extension for selected label */
    description?: React.ReactNode;
}

interface IState {
    hasFocus: boolean;
    isOpen: boolean;
    selectedIndex: number;
    selectedOption: any;
    keyword: string;
}

/**
 * Regular HTML select field with extended functionality and styling. Use this within form row. Used for selecting one option from the list. Use pillbutton layout if you have up to 5 items with short labels to choose from.
 */
export default class ControlSelect extends React.Component<IControlSelectProps, IState> {
    static displayName: string = 'ControlSelect';

    private nodeMain;
    private nodePopover;
    private nodeSelect;
    private searchfield;
    private didFocus: boolean = false;
    private optionsLength: number = 0;

    private allValues: {
        label: string;
        value: string;
        className?: string;
        type: string;
    }[] = [];
    private filteredValues: {
        label: string;
        value: string;
        className?: string;
    }[] = [];

    private selectedValueLabel = '';

    constructor (props: IControlSelectProps) {
        super(props);

        this.state = {
            hasFocus: false,
            isOpen: false,
            selectedIndex: -1,
            selectedOption: null,
            keyword: '',
        };
    }

    handleClosePopover = (e) => {
        if (e && e.type === 'click') {
            return;
        }
        this.setState({
            isOpen: false,
        });
    };

    handleKeypress = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            this.handleArrowDown(e);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            this.handleArrowUp(e);
        } else if (e.key === 'Escape') {
            this.handleEscape(e);
        } else if (e.key === 'Enter') {
            this.handleEnter(e);
        } else if (e.key === 'Tab') {
            this.handleTab(e);
        }
    };

    handleKeypressClosed = (e) => {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter') {
            e.preventDefault();
            this.setState({
                isOpen: true,
            });
        } else if (e.key === 'Escape') {
            this.handleEscape(e);
        }
    };

    handleArrowDown = (e) => {
        e.preventDefault();
        let newIndex = this.state.selectedIndex;
        if (this.optionsLength <= this.state.selectedIndex + 1) {
            newIndex = 0;
        } else {
            newIndex = this.state.selectedIndex + 1;
        }
        this.setState({
            selectedIndex: newIndex,
        });
    };

    handleArrowUp = (e) => {
        e.preventDefault();
        let newIndex = this.state.selectedIndex;
        if (this.state.selectedIndex - 1 < 0) {
            newIndex = this.optionsLength - 1;
        } else {
            newIndex = this.state.selectedIndex - 1;
        }
        this.setState({
            selectedIndex: newIndex,
        });
    };

    handleEscape = (e) => {
        e.preventDefault();
        this.setState({
            isOpen: false,
        });
    };

    handleEnter = (e) => {
        e.preventDefault();
        this.setState({
            isOpen: false,
        });
        if (
            this.state.selectedIndex !== -1 &&
            this.filteredValues.length > this.state.selectedIndex &&
            this.props.onChange
        ) {
            // Set value selected with arrow keys
            this.setValue(this.filteredValues[this.state.selectedIndex].value, this.filteredValues[this.state.selectedIndex].label);
            if (this.nodeMain) {
                this.nodeMain.focus();
            }
        } else if (
            this.state.selectedIndex === -1 &&
            this.filteredValues.length > 0 &&
            this.props.onChange
        ) {
            // There is no value selected with arrow keys, select first
            this.setValue(this.filteredValues[0].value, this.filteredValues[0].label);
            if (this.nodeMain) {
                this.nodeMain.focus();
            }
        }
    };

    handleTab = (e) => {
        if (this.nodeMain) {
            const shiftPressed = e.shiftKey;
            const allFocusables = document.querySelectorAll<HTMLElement>('button:not([tabindex="-1"]), A[href]:not([tabindex="-1"]), input:not([tabindex="-1"]), select:not([tabindex="-1"]), textarea:not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])');
            const currentIndex = findIndex(allFocusables, this.nodeMain);

            if (shiftPressed) {
                if (currentIndex !== -1 && allFocusables.length > currentIndex - 1) {
                    e.preventDefault();
                    allFocusables[currentIndex - 1].focus();
                }
            } else {
                if (currentIndex !== -1 && allFocusables.length > currentIndex + 1) {
                    e.preventDefault();
                    allFocusables[currentIndex + 1].focus();
                }
            }
            this.setState({
                isOpen: false,
                hasFocus: false,
            });
        }
    };

    setFocus = () => {
        if (this.nodeMain) {
            this.nodeMain.focus();
        } else if (this.searchfield) {
            this.searchfield.focus();
        }
    };

    setBlur = () => {
        if (this.nodeMain) {
            this.nodeMain.focus();
            this.nodeMain.blur();
        } else if (this.nodeSelect) {
            this.nodeSelect.focus();
            this.nodeSelect.blur();
        }
    };

    setValue = (val, label) => {
        const e = {
            target: {
                value: val,
                name: this.props.name,
                id: this.props.id,
            },
        };
        if (this.props.onChange) {
            this.props.onChange(e);
        }
        this.selectedValueLabel = label;
        this.setState({
            keyword: label,
        });
    };

    handleResize = () => {
        if (this.nodePopover && this.nodeSelect) {
            const selectWidth = this.nodeSelect.offsetWidth;
            this.nodePopover.style.maxWidth = selectWidth + 'px';
            this.nodePopover.style.width = '100%';
        }
    };

    componentDidUpdate (prevProps: Readonly<IControlSelectProps>, prevState: Readonly<IState>): void {
        if (!prevState.isOpen && this.state.isOpen) {
            this.setState({
                selectedIndex: -1,
                selectedOption: null,
            });
            document.addEventListener('keydown', this.handleKeypress);
            document.removeEventListener('keydown', this.handleKeypressClosed);
        }

        if (!this.state.isOpen && this.state.hasFocus) {
            document.addEventListener('keydown', this.handleKeypressClosed);
        }

        if (prevState.isOpen && !this.state.isOpen) {
            document.removeEventListener('keydown', this.handleKeypress);
        }

        if (!this.state.hasFocus && !this.state.isOpen) {
            document.removeEventListener('keydown', this.handleKeypress);
            document.removeEventListener('keydown', this.handleKeypressClosed);
        }
    }

    componentWillUnmount (): void {
        document.removeEventListener('keydown', this.handleKeypress);
        document.removeEventListener('keydown', this.handleKeypressClosed);
        window.removeEventListener('resize', this.handleResize);
    }

    render () {
        let hasValue = true;
        let canClear = false;
        let searchEnabled = false;

        this.allValues = [];
        this.filteredValues = [];
        this.selectedValueLabel = this.props.value;

        const mapChildren = (children) => {
            React.Children.map(children, (child: any, index) => {
                if (typeof child === 'string' || child === null) {
                    return;
                }

                if (child.type === 'optgroup') {
                    const newVal = {
                        value: child.props.label,
                        label: child.props.label,
                        className: child.props.className,
                        type: 'optgroup',
                    };
                    this.allValues.push(newVal);
                    mapChildren(child.props.children);
                } else if (child.type === 'option') {
                    const newVal = {
                        value: child.props.value ? child.props.value : (child.props.children ? child.props.children : ''),
                        label: child.props.children ? child.props.children : '',
                        className: child.props.className,
                        type: 'option',
                    };
                    if (newVal.value === this.props.value) {
                        this.selectedValueLabel = newVal.label;
                    }

                    if (index === 0 && newVal.value === '' && newVal.label === '') {
                        canClear = true;
                        return;
                    }

                    this.allValues.push(newVal);
                }
            });
        };

        mapChildren(this.props.children);

        if (this.allValues.length > 10 && !this.props.disableSearch) {
            searchEnabled = true;
            if (this.props.layout !== 'select' && this.props.layout !== 'pillbutton') {
                this.allValues.map((val, index, arr) => {
                    if (this.state.keyword.length > 0) {
                        if (val.type === 'optgroup') {
                            const a = drop(arr, index + 1);
                            const nextOptions = takeWhile(a, (elem) => {
                                return elem.type === 'option';
                            });
                            const filteredNextOptions = nextOptions.filter((o) => o.label.toLowerCase().includes(this.state.keyword.toLowerCase()));
                            if (filteredNextOptions.length === 0) {
                                return;
                            }
                        } else {
                            if (!val.label.toLowerCase().includes(this.state.keyword.toLowerCase())) {
                                return;
                            }
                        }
                    }
                    this.filteredValues.push(val);
                });
            }
        } else {
            this.filteredValues = this.allValues;
        }

        this.optionsLength = this.filteredValues.length;

        if (this.props.value === '') {
            hasValue = false;
        }

        if (this.filteredValues.length > 0 && this.filteredValues[0].value === '' && this.filteredValues[0].label === '' && this.props.value !== '') {
            canClear = true;
        }

        return (
            <React.Fragment>
                {!this.props.layout && (
                    <Popover
                        target={(
                            <div
                                className="control-select-inner"
                                data-testid={this.props.testId}
                                onClick={(e) => {
                                    if (this.props.disabled) {
                                        return;
                                    }
                                    e.preventDefault();
                                    if (!this.state.isOpen) {
                                        this.setState({
                                            isOpen: true,
                                            keyword: this.selectedValueLabel,
                                        });
                                    } else if (!searchEnabled) {
                                        this.setState({
                                            isOpen: false,
                                        });
                                    }
                                }}
                                ref={(e) => {
                                    if (e) {
                                        this.nodeSelect = e;
                                    }
                                }}
                                onFocus={() => {
                                    if (this.props.disabled) {
                                        return;
                                    }
                                    if (!this.state.hasFocus) {
                                        this.setState({
                                            hasFocus: true,
                                            keyword: this.selectedValueLabel,
                                        });
                                        if (this.searchfield) {
                                            this.searchfield.focus();
                                        }
                                    }
                                }}
                                onBlur={() => {
                                    if (this.state.hasFocus) {
                                        this.setState({
                                            hasFocus: false,
                                        });
                                    }
                                }}
                            >
                                {searchEnabled ? (
                                    <React.Fragment>
                                        <ControlInput
                                            autoFocus={this.props.autoFocus}
                                            value={this.state.hasFocus || this.state.isOpen ? this.state.keyword : this.selectedValueLabel}
                                            onChange={(event) => {
                                                this.setState({
                                                    isOpen: true,
                                                    keyword: event.target.value,
                                                });
                                            }}
                                            inputRef={(e) => {
                                                this.searchfield = e;
                                            }}
                                            hasError={this.props.hasError}
                                            disabled={this.props.disabled}
                                            placeholder={this.props.placeholder}
                                            onFocus={() => {
                                                if (this.props.selectAllOnFocus && this.searchfield) {
                                                    this.searchfield.select();
                                                }
                                            }}
                                        />
                                        {!this.props.disableClear && canClear && (this.state.keyword !== '' || this.selectedValueLabel !== '') && (
                                            <button
                                                disabled={this.props.disabled}
                                                className="control-select-addon"
                                                tabIndex={-1}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    this.setState({
                                                        isOpen: !this.state.isOpen,
                                                    });
                                                    if (this.state.hasFocus && this.state.isOpen) {
                                                        this.setState({
                                                            keyword: '',
                                                        });
                                                    } else {
                                                        e.stopPropagation();
                                                        this.setValue('', '');
                                                    }
                                                }}
                                            >
                                                <Icon
                                                    width={16}
                                                    height={16}
                                                    kind="clear"
                                                />
                                            </button>
                                        )}
                                        <button
                                            className={classNames('control-select-addon')}
                                            tabIndex={-1}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                this.handleEscape(e);
                                                this.setFocus();
                                                this.setState({
                                                    isOpen: !this.state.isOpen,
                                                });
                                            }}
                                        >
                                            <Icon
                                                width={10}
                                                height={10}
                                                kind={this.state.isOpen && this.filteredValues.length > 0 ? 'arrow1-up' : 'arrow1-down'}
                                            />
                                        </button>
                                    </React.Fragment>
                                ) : (
                                    <React.Fragment>
                                        <div
                                            className={classNames('control-select-select', { 'has-placeholder': this.props.placeholder && this.selectedValueLabel === '' })}
                                            tabIndex={this.props.disabled ? -1 : 0}
                                            ref={(e) => {
                                                if (e) {
                                                    this.nodeMain = e;
                                                }
                                            }}
                                        >
                                            {!!this.props.description && (<span className="control-select-description">{this.props.description}</span>)}
                                            {this.props.placeholder && this.selectedValueLabel === '' ? (<span>{this.props.placeholder}</span>) : ''}
                                            {this.selectedValueLabel && (<span>{this.selectedValueLabel}</span>)}
                                        </div>
                                        {!this.props.disableClear && canClear && this.props.value !== '' && (
                                            <button
                                                disabled={this.props.disabled}
                                                className={classNames('control-select-addon', Classes.POPOVER_DISMISS_OVERRIDE)}
                                                tabIndex={-1}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    this.setBlur();
                                                    this.setState({
                                                        isOpen: !this.state.isOpen,
                                                    });
                                                    if (this.state.hasFocus && this.state.isOpen) {
                                                        this.setState({
                                                            keyword: '',
                                                        });
                                                    } else {
                                                        e.stopPropagation();
                                                        this.setValue('', '');
                                                    }
                                                }}
                                            >
                                                <Icon
                                                    width={16}
                                                    height={16}
                                                    kind="clear"
                                                />
                                            </button>
                                        )}
                                        <Icon
                                            width={10}
                                            height={10}
                                            kind={this.state.isOpen && this.filteredValues.length > 0 ? 'arrow1-up' : 'arrow1-down'}
                                            wrapperClassName="control-select-addon"
                                        />
                                    </React.Fragment>
                                )}
                            </div>
                        )}
                        content={(
                            <Menu>
                                {this.filteredValues.map((child: any, index) => {
                                    if (child.type === 'optgroup') {
                                        return (
                                            <MenuDivider key={index} title={child.label}/>
                                        );
                                    }
                                    return (
                                        <MenuItem
                                            key={index}
                                            text={child.label === '' ? '\u00a0' : (
                                                this.state.keyword !== '' && this.state.keyword.length >= 2 && searchEnabled && this.state.isOpen ? (
                                                    <Highlight
                                                        search={this.state.keyword}
                                                        ignoreDiacritics={true}
                                                    >{child.label}</Highlight>
                                                ) : child.label
                                            )}
                                            disabled={this.props.disabled}
                                            onMouseDown={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                this.setFocus();
                                                this.setValue(child.value, child.label);
                                                this.setState({
                                                    isOpen: false,
                                                });
                                            }}
                                            className={classNames({
                                                active: child.value === this.props.value,
                                                highlight: index === this.state.selectedIndex,
                                            }, child.className)}
                                            innerRef={(elem) => {
                                                if (elem && index === this.state.selectedIndex) {
                                                    const domElem = findDOMNode(elem) as HTMLElement;
                                                    const domElemParent = getScrollParent(domElem) as HTMLElement;
                                                    scrollTo(domElemParent, {
                                                        top: offsetTill(domElem, domElemParent) - (domElemParent.getBoundingClientRect().height / 2) + (domElem.getBoundingClientRect().height / 2),
                                                    }).then(() => {
                                                    });
                                                }
                                            }}
                                        />
                                    );
                                })}
                            </Menu>
                        )}
                        disabled={this.props.disabled}
                        openOnTargetFocus={false}
                        hasBackdrop={false}
                        isOpen={this.state.isOpen && this.filteredValues.length > 0}
                        onClose={this.handleClosePopover}
                        portalClassName={'control-select-portal'}
                        popoverClassName={'control-select-popover'}
                        position={Position.BOTTOM_LEFT}
                        minimal={true}
                        usePortal={true}
                        enforceFocus={false}
                        modifiers={{
                            offset: {
                                offset: '0, 5px',
                                order: 201,
                            },
                            inner: {
                                enabled: false,
                            },
                            flip: {
                                enabled: false,
                            },
                            shift: {
                                enabled: false,
                            },
                            preventOverflow: {
                                enabled: false,
                            },
                            hide: {
                                enabled: false,
                            },
                            applyStyle: {
                                gpuAcceleration: false,
                            },
                            computeStyle: {
                                gpuAcceleration: false,
                            },
                        }}
                        wrapperTagName="div"
                        className={classNames('control-select', {
                            'has-error': this.props.hasError,
                            'has-value': hasValue,
                            'focus': this.state.hasFocus || this.state.isOpen,
                            'disabled': this.props.disabled,
                            'size-default': !this.props.size || this.props.size === 'default',
                            'size-small': this.props.size === 'small',
                        }, this.props.className)}
                        popoverRef={(e) => {
                            if (e) {
                                this.nodePopover = e;
                                window.addEventListener('resize', this.handleResize);
                                this.handleResize();
                            }
                        }}
                        transitionDuration={0}
                    />
                )}
                {this.props.layout === 'menu' && (
                    <Popover
                        target={(
                            <div
                                className="control-select-menu-inner"
                                onClick={(e) => {
                                    if (this.props.disabled) {
                                        return;
                                    }
                                    e.preventDefault();
                                    if (!this.state.isOpen) {
                                        this.setState({
                                            isOpen: true,
                                            keyword: '',
                                        });
                                    } else if (this.state.isOpen) {
                                        this.setState({
                                            isOpen: false,
                                        }, () => {
                                            this.setFocus();
                                        });
                                    }
                                }}
                                ref={(e) => {
                                    if (e) {
                                        this.nodeSelect = e;
                                    }
                                }}
                                onFocus={() => {
                                    if (this.props.disabled) {
                                        return;
                                    }
                                    if (!this.state.hasFocus) {
                                        this.setState({
                                            hasFocus: true,
                                            keyword: '',
                                        });
                                        if (this.searchfield) {
                                            this.searchfield.focus();
                                        }
                                    }
                                }}
                                onBlur={() => {
                                    if (this.state.hasFocus) {
                                        this.setState({
                                            hasFocus: false,
                                        });
                                    }
                                }}
                            >
                                <div
                                    className={classNames('control-select-menu-select', { 'has-placeholder': this.props.placeholder && this.selectedValueLabel === '' })}
                                    tabIndex={this.props.disabled ? -1 : 0}
                                    ref={(e) => {
                                        if (e) {
                                            this.nodeMain = e;
                                        }
                                    }}
                                >
                                    {this.props.placeholder && this.selectedValueLabel === '' ? this.props.placeholder : ''}
                                    {this.selectedValueLabel}
                                </div>
                                <Icon
                                    width={10}
                                    height={10}
                                    kind={this.state.isOpen && this.filteredValues.length > 0 ? 'arrow3-up' : 'arrow3-down'}
                                    wrapperClassName="control-select-menu-addon"
                                />
                            </div>
                        )}
                        content={(
                            <React.Fragment>
                                {searchEnabled && (
                                    <div className="menu-search">
                                        <ControlInput
                                            autoFocus={true}
                                            value={this.state.keyword}
                                            onChange={(event) => {
                                                this.setState({
                                                    isOpen: true,
                                                    keyword: event.target.value,
                                                });
                                            }}
                                            inputRef={(e) => {
                                                this.searchfield = e;
                                            }}
                                            hasError={this.props.hasError}
                                            disabled={this.props.disabled}
                                            placeholder={this.props.placeholder}
                                            onFocus={() => {
                                                if (this.props.selectAllOnFocus && this.searchfield) {
                                                    this.searchfield.select();
                                                }
                                            }}
                                        />
                                    </div>
                                )}
                                <Menu>
                                    {this.filteredValues.map((child: any, index) => {
                                        return (
                                            <MenuItem
                                                key={index}
                                                text={child.label === '' ? '\u00a0' : (
                                                    this.state.keyword !== '' && this.state.keyword.length >= 2 && searchEnabled && this.state.isOpen ? (
                                                        <Highlight
                                                            search={this.state.keyword}
                                                            ignoreDiacritics={true}
                                                        >{child.label}</Highlight>
                                                    ) : child.label
                                                )}
                                                disabled={this.props.disabled}
                                                onMouseDown={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    this.setFocus();
                                                    this.setValue(child.value, child.label);
                                                    this.setState({
                                                        isOpen: false,
                                                    });
                                                }}
                                                className={classNames({
                                                    active: child.value === this.props.value,
                                                    highlight: index === this.state.selectedIndex,
                                                }, child.className)}
                                                innerRef={(elem) => {
                                                    if (elem && index === this.state.selectedIndex) {
                                                        const domElem = findDOMNode(elem) as HTMLElement;
                                                        const domElemParent = getScrollParent(domElem) as HTMLElement;
                                                        scrollTo(domElemParent, {
                                                            top: offsetTill(domElem, domElemParent) - (domElemParent.getBoundingClientRect().height / 2) + (domElem.getBoundingClientRect().height / 2),
                                                        }).then(() => {
                                                        });
                                                    }
                                                }}
                                            />
                                        );
                                    })}
                                </Menu>
                            </React.Fragment>
                        )}
                        disabled={this.props.disabled}
                        openOnTargetFocus={false}
                        hasBackdrop={false}
                        isOpen={this.state.isOpen}
                        onClose={this.handleClosePopover}
                        portalClassName={'control-select-portal'}
                        popoverClassName={'control-select-popover'}
                        position={Position.BOTTOM_LEFT}
                        minimal={true}
                        usePortal={true}
                        enforceFocus={false}
                        modifiers={{
                            offset: {
                                offset: '0, 5px',
                                order: 201,
                            },
                            inner: {
                                enabled: false,
                            },
                            flip: {
                                enabled: false,
                            },
                            preventOverflow: {
                                enabled: false,
                            },
                            hide: {
                                enabled: false,
                            },
                        }}
                        wrapperTagName="div"
                        className={classNames('control-select-menu', {
                            'has-error': this.props.hasError,
                            'has-value': hasValue,
                            'focus': this.state.hasFocus || this.state.isOpen,
                            'disabled': this.props.disabled,
                        }, this.props.className)}
                        popoverRef={(e) => {
                            if (e) {
                                this.nodePopover = e;
                            }
                        }}
                        transitionDuration={250}
                    />
                )}
                {this.props.layout === 'select' && (
                    <div className={classNames('control-select', {
                        'has-error': this.props.hasError,
                        'focus': this.state.hasFocus,
                    }, this.props.className)}>
                        <div className="control-select-inner"
                             data-testid={this.props.testId}>
                            <select
                                placeholder={this.props.placeholder}
                                className="control-select-select"
                                value={this.props.value}
                                disabled={this.props.disabled}
                                onFocus={(e) => {
                                    if (this.props.disabled) {
                                        return;
                                    }
                                    this.setState({
                                        hasFocus: true,
                                    });
                                    if (this.props.onFocus) {
                                        this.props.onFocus(e);
                                    }
                                }}
                                onBlur={(e) => {
                                    this.setState({
                                        hasFocus: false,
                                    });
                                    if (this.props.onBlur) {
                                        this.props.onBlur(e);
                                    }
                                }}
                                onChange={this.props.onChange}
                                ref={(e) => {
                                    if (typeof e === 'object' && e !== null && this.props.autoFocus && !this.didFocus) {
                                        e.focus();
                                        this.didFocus = true;
                                    }
                                }}>
                                {this.props.children}
                            </select>
                            <Icon width={10} height={10} kind="arrow1-down" wrapperClassName="control-select-addon"/>
                        </div>
                    </div>
                )}
                {this.props.layout === 'pillbutton' && (
                    <React.Fragment>
                        <div className={classNames('control-select-pillbutton', {
                            'has-value': this.props.value !== '',
                            'has-error': this.props.hasError,
                        }, this.props.className)}>
                            {React.Children.map(this.props.children, (child) => {
                                if (typeof child === 'string' || child === null) {
                                    return child;
                                }
                                if (child.type === 'optgroup') {
                                    let isActive = false;
                                    let groupLabel = child.props.label;
                                    React.Children.map(child.props.children, (c) => {
                                        if (typeof c === 'string' || c === null) {
                                            return;
                                        }
                                        if (c.props.value === this.props.value) {
                                            isActive = true;
                                            groupLabel = c.props.children;
                                        }
                                    });
                                    return (
                                        <Popover
                                            content={(
                                                <Menu className="pillbutton">
                                                    {React.Children.map(child.props.children, (c) => {
                                                        if (typeof c === 'string' || c === null) {
                                                            return c;
                                                        }
                                                        return (
                                                            <MenuItem
                                                                text={c.props.children}
                                                                disabled={this.props.disabled}
                                                                active={c.props.value === this.props.value}
                                                                onClick={() => {
                                                                    const e = {
                                                                        target: {
                                                                            value: c.props.value,
                                                                            name: this.props.name,
                                                                            id: this.props.id,
                                                                        },
                                                                    };
                                                                    if (this.props.onChange) {
                                                                        this.props.onChange(e);
                                                                    }
                                                                    if (this.props.onClick) {
                                                                        this.props.onClick(e);
                                                                    }
                                                                }}
                                                            />
                                                        );
                                                    })}
                                                </Menu>
                                            )}
                                            target={(
                                                <div>
                                                    {groupLabel}
                                                    <Icon width={8} height={8} kind="arrow3-down"/>
                                                </div>
                                            )}
                                            hasBackdrop={false}
                                            portalClassName={'popover-default-portal'}
                                            popoverClassName={'popover-default-popover nopadding'}
                                            position={BlueprintPosition.BOTTOM}
                                            minimal={true}
                                            usePortal={true}
                                            modifiers={{
                                                offset: {
                                                    offset: '0, 2',
                                                },
                                            }}
                                            wrapperTagName="div"
                                            className={classNames('control-select-pillbutton-item', { active: isActive, })}
                                        />
                                    );
                                } else {
                                    return (
                                        <label className="control-select-pillbutton-item">
                                            <input
                                                type="radio"
                                                value={child.props.value}
                                                checked={child.props.value.toString() === this.props.value.toString()}
                                                disabled={this.props.disabled}
                                                onChange={this.props.onChange}
                                                onClick={this.props.onClick}
                                            />
                                            <span>{child.props.children}</span>
                                        </label>
                                    );
                                }
                            })}
                        </div>
                    </React.Fragment>
                )}
            </React.Fragment>
        );
    }
}
